import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  InputAdornment,
  Switch,
  FormControlLabel,
  Modal,
  LinearProgress,
  IconButton,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Layout components
import DashboardLayout from 'composites/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'composites/Navbars/DashboardNavbar';

// Auth context

// Types
import { CryptoCurrency } from '../../../../packages/crypto-payments/src/models/types';
import { useUser } from 'context/user';
import { CRYPTO_PAYMENTS_API_URL, FIREBASE_API_URL } from 'config';

const PAYMENT_TIMEOUT_MINUTES = 30;

interface PaymentResponse {
  paymentId: string;
  systemAddress: string;
  senderAddress: string;
  currency: CryptoCurrency;
  amount: number;
  rawAmount: number;
  paymentIdentifier: string;
  expiresAt: string;
  usdEquivalent?: number;
  exchangeRate?: number;
  credits: number;
}

interface PaymentStatusResponse {
  paymentId: string;
  status: 'pending' | 'confirmed' | 'failed';
  currency: CryptoCurrency;
  amount: number;
  address: string;
  paymentIdentifier: string;
  transactionHash?: string;
  confirmations?: number;
  credits: number;
  createdAt: string;
  updatedAt: string;
  confirmedAt?: string;
}

// Payment Tracking Modal Component
export default function PaymentTrackingModal({
  open,
  payment,
  onClose,
  onSuccess,
}: {
  open: boolean;
  payment: PaymentResponse | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [timeLeft, setTimeLeft] = useState<number>(
    PAYMENT_TIMEOUT_MINUTES * 60
  );
  const [progress, setProgress] = useState<number>(100);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const { accessToken, user } = useUser();

  // Start timer and polling when modal opens
  useEffect(() => {
    if (open && payment) {
      // Reset states
      setTimeLeft(PAYMENT_TIMEOUT_MINUTES * 60);
      setProgress(100);
      setPaymentStatus(null);
      setError(null);
      setIsLoading(true);
      setIsSuccess(false);

      // Start countdown timer
      timerRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          const newTime = prev - 1;
          const newProgress = (newTime / (PAYMENT_TIMEOUT_MINUTES * 60)) * 100;
          setProgress(newProgress);

          if (newTime <= 0) {
            if (timerRef.current) clearInterval(timerRef.current);
            if (!isSuccess) {
              setError('Payment timeout. Please try again.');
              setIsLoading(false);
            }
            return 0;
          }
          return newTime;
        });
      }, 1000);

      // Start payment status polling
      startPollingPaymentStatus(accessToken);

      return () => {
        // Clean up on unmount or when modal closes
        if (timerRef.current) clearInterval(timerRef.current);
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
  }, [open, payment, accessToken]);

  // Format time left as MM:SS
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  // Start polling payment status
  const startPollingPaymentStatus = async (token: string) => {
    if (!payment || !token) return;

    // Initial check
    await checkPaymentStatus(token);

    // Set up polling every 60 seconds
    pollingRef.current = setInterval(async () => {
      await checkPaymentStatus(token);
    }, 60000);
  };

  // Check payment status
  const checkPaymentStatus = async (token: string) => {
    if (!payment || !token) return;

    try {
      // First, trigger the monitoring for the specific currency
      await fetch(
        `${CRYPTO_PAYMENTS_API_URL}/api/crypto/monitor/${payment.currency.toLowerCase()}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accessToken: token,
            paymentIdentifier: payment.paymentIdentifier,
            senderAddress: payment.senderAddress,
            rawAmount: payment.rawAmount,
          }),
        }
      );

      // Then check the payment status
      const response = await fetch(
        `${CRYPTO_PAYMENTS_API_URL}/api/crypto/payment/${payment.paymentId}?accessToken=${token}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch payment status');
      }

      const data: PaymentStatusResponse = await response.json();
      setPaymentStatus(data.status);

      // Update status message based on payment status
      if (data.status === 'confirmed') {
        setStatusMessage(
          'Payment confirmed! Adding credits to your account...'
        );

        // Add credits to user's account
        await addCreditsToUserAccount(payment, token);

        // Clear intervals and show success
        if (timerRef.current) clearInterval(timerRef.current);
        if (pollingRef.current) clearInterval(pollingRef.current);

        setIsSuccess(true);
        setIsLoading(false);
        setStatusMessage('Credits added to your account successfully!');

        // Notify parent component of success
        onSuccess();
      } else if (data.status === 'failed') {
        setError('Payment failed. Please try again.');
        setIsLoading(false);

        if (timerRef.current) clearInterval(timerRef.current);
        if (pollingRef.current) clearInterval(pollingRef.current);
      } else {
        // Still pending
        if (data.transactionHash) {
          setStatusMessage(
            `Transaction detected! Waiting for confirmations (${
              data.confirmations || 0
            })...`
          );
        }
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollingRef.current) clearInterval(pollingRef.current);
    }
  };

  // Add credits to user account
  const addCreditsToUserAccount = async (
    payment: PaymentResponse,
    token: string
  ) => {
    if (!token) return;

    try {
      // This is a placeholder - the actual implementation would depend on your Firebase setup
      // You would typically have an API endpoint to add credits to the user's account

      // Example API call to add credits
      const response = await fetch(`${FIREBASE_API_URL}/api/user/credits/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          accessToken: token,
          paymentId: payment.paymentId,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to add credits to account');
      }

      return await response.json();
    } catch (err) {
      console.error('Error adding credits to account:', err);
      setError(
        'Failed to add credits to your account. Please contact support.'
      );
      setIsLoading(false);
      throw err;
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!isLoading || error) {
          onClose();
        }
      }}
      aria-labelledby="payment-tracking-modal"
      aria-describedby="modal-to-track-crypto-payment"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: 750 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography
          id="payment-tracking-modal"
          variant="h6"
          component="h2"
          gutterBottom
        >
          Payment Tracking
        </Typography>

        <Box sx={{ mb: 2 }}>
          <progress
            value={progress}
            max={100}
            style={{ width: '100%' }}
            color="success"
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Time remaining
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatTimeLeft()}
            </Typography>
          </Box>
        </Box>

        {!error && (
          <Alert severity={'info'} sx={{ mb: 2, mt: 2 }}>
            {statusMessage ||
              'Please do not close this window while payment is being proccessed otherwise we will not be able to confirm it automatically.'}
          </Alert>
        )}

        {error && (
          <Alert severity={'error'} sx={{ mb: 2 }}>
            There was a problem processing your payment. <br />
            Please get in touch with us at{' '}
            <a href="mailto:x@xtvultra.com">x@xtvultra.com</a> providing your
            payment id: <b>{`${payment.paymentIdentifier}`}</b> and we will add
            the credits to your account manually.
          </Alert>
        )}

        {payment && (
          <Paper
            elevation={0}
            sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Payment Details:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mr: 1 }}
              >
                Wallet address:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  bgcolor: 'grey.200',
                  px: 1,
                  py: 0.5,
                  borderRadius: 0.5,
                  flex: 1,
                  wordBreak: 'break-all',
                }}
              >
                {payment.systemAddress}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(payment.systemAddress);
                }}
                sx={{ ml: 1 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mr: 1 }}
              >
                Amount to send:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                }}
              >
                {payment.amount} {payment.currency}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mr: 1 }}
              >
                You will receive:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  color: 'success.main',
                }}
              >
                {payment.credits} credits
              </Typography>
            </Box>
          </Paper>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color={isSuccess ? 'success' : 'primary'}
            disabled={isLoading && !error}
            onClick={onClose}
          >
            {isSuccess ? 'Done' : error ? 'Close' : 'Please Wait...'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
